import { t } from "i18next";

import { LIVE_MAP_MAX_SELECTED_RIDES } from "../constants/live-map.const";
import { IExtendableDto } from "../dto";
import { RydeUiUnit } from "../models";
import { Log } from "./log.helpers";

/** Rides search grid and live map: bulk select updates the side map. */

export const isEntityWithGridMapBulkAutofit = (entity: RydeUiUnit): boolean => {
  return entity === "ride" || entity === "rideMap";
};

export const isEntitySelectionKeptOnSearch = (entity: RydeUiUnit): boolean => {
  return entity === "passenger";
};

export const isEntityWithTableSelectionLimit = (
  entity: RydeUiUnit,
): boolean => {
  return entity === "rideMap";
};

export const getTableSelectionLimit = (
  entity: RydeUiUnit,
): number | undefined => {
  if (isEntityWithTableSelectionLimit(entity)) {
    return LIVE_MAP_MAX_SELECTED_RIDES;
  }

  return undefined;
};

export const isTableSelectionAtLimit = (
  selectedIds: readonly number[],
  entity: RydeUiUnit,
): boolean => {
  const limit = getTableSelectionLimit(entity);

  return limit !== undefined && selectedIds.length >= limit;
};

export const warnTableSelectionLimitReached = (entity: RydeUiUnit): void => {
  const limit = getTableSelectionLimit(entity);

  if (limit === undefined) {
    return;
  }

  Log.warnAndToast(
    t("liveMap.MAX_SELECTED_RIDES_REACHED", {
      max: limit,
    }),
  );
};

const countMergedTableSelection = ({
  prev,
  idsToAdd,
}: {
  prev: readonly number[];
  idsToAdd: readonly number[];
}): number => {
  const next = new Set(prev);

  for (const id of idsToAdd) {
    next.add(id);
  }

  return next.size;
};

export const wouldExceedTableSelectionLimit = (
  prev: readonly number[],
  idsToAdd: readonly number[],
  entity: RydeUiUnit,
): boolean => {
  const limit = getTableSelectionLimit(entity);

  if (limit === undefined || !idsToAdd.length) {
    return false;
  }

  return countMergedTableSelection({ prev, idsToAdd }) > limit;
};

export const addIdsToTableSelection = (
  prev: number[],
  idsToAdd: readonly number[],
  entity?: RydeUiUnit,
): number[] => {
  if (!idsToAdd.length) {
    return prev;
  }

  if (entity && wouldExceedTableSelectionLimit(prev, idsToAdd, entity)) {
    warnTableSelectionLimitReached(entity);

    return prev;
  }

  const next = new Set(prev);

  for (const id of idsToAdd) {
    next.add(id);
  }

  return Array.from(next);
};

export const rejectTableSelectionOverLimit = (
  selectedIds: readonly number[],
  entity: RydeUiUnit,
): number[] | undefined => {
  const limit = getTableSelectionLimit(entity);

  if (limit === undefined || selectedIds.length <= limit) {
    return [...selectedIds];
  }

  warnTableSelectionLimitReached(entity);

  return undefined;
};

export const removeIdsFromTableSelection = (
  prev: number[],
  idsToRemove: readonly number[],
): number[] => {
  if (!idsToRemove.length) {
    return prev;
  }

  const removeSet = new Set(idsToRemove);

  return prev.filter(id => !removeSet.has(id));
};

export const areAllIdsInTableSelection = (
  selectedIdsSet: Set<number>,
  ids: readonly number[],
): boolean => {
  return ids.length > 0 && ids.every(id => selectedIdsSet.has(id));
};

/**
 * Client grids prune checkboxes to rows still in the loaded scope.
 * Server pagination only has the current page in memory, so the same prune drops a bulk selection.
 */
export const resolveSelectionAfterLoadedRowsChange = ({
  selectedIds,
  loadedIds,
  keepOffLoadedPage,
}: {
  selectedIds: readonly number[];
  loadedIds: ReadonlySet<number>;
  keepOffLoadedPage: boolean;
}): number[] => {
  if (keepOffLoadedPage) {
    return [...selectedIds];
  }

  return selectedIds.filter(id => loadedIds.has(id));
};

/**
 * Join selected ids to loaded DTOs. Grouped server pages store rows in group buckets, not the flat atom.
 */
export const resolveSelectedTableRowValues = ({
  selectedIds,
  rows,
}: {
  selectedIds: readonly number[];
  rows: readonly IExtendableDto[];
}): IExtendableDto[] => {
  if (!selectedIds.length || !rows.length) {
    return [];
  }

  const selectedIdsSet = new Set(selectedIds);

  return rows.filter(row => selectedIdsSet.has(row.id));
};

export const flattenGroupedTableRows = (
  groupedRowsMap: Readonly<
    Record<string, readonly IExtendableDto[] | undefined>
  >,
): IExtendableDto[] => {
  return Object.values(groupedRowsMap).flatMap(groupRows => groupRows ?? []);
};
